/**
 * Плагин: live-геометрия во время жеста, persist updateNode только на отпускании при ненулевой дельте.
 */
import {JUNCTION, AND_JUNCTION, OR_JUNCTION} from '../modelerX6/constants';

const LIVE_THROTTLE_MS = 80;

export default {
	init(modeler, context) {
		const {graph, onPatch, onLive, onLock, viewIndex} = context;
		const VIEW_INDEX = viewIndex ?? 0;
		const pending = new Map();
		let liveTimer = null;
		let lastLiveAt = 0;

		const readGeometry = (cell, viewElement) => {
			const elemType = modeler._getType(viewElement);
			const isJunction = [JUNCTION, AND_JUNCTION, OR_JUNCTION].includes(elemType);
			const pos = cell.getPosition?.();
			const size = cell.getSize?.();
			if (!pos && !size) return null;
			const x = pos ? Math.round(pos.x) : (viewElement.bounds?.x ?? 0);
			const y = pos ? Math.round(pos.y) : (viewElement.bounds?.y ?? 0);
			let w = size
				? Math.round(size.width)
				: (viewElement.bounds?.width ?? viewElement.bounds?.w ?? 120);
			let h = size
				? Math.round(size.height)
				: (viewElement.bounds?.height ?? viewElement.bounds?.h ?? 60);
			if (isJunction) {
				w = 20;
				h = 20;
			}
			const x6Parent = cell.getParent?.();
			const parentNodeId =
				x6Parent && typeof x6Parent.isNode === 'function' && x6Parent.isNode()
					? (x6Parent.id ?? null)
					: null;
			return {x, y, w, h, parentNodeId};
		};

		const applyLocalModel = (id, viewElement, geo) => {
			if (viewElement.bounds) {
				viewElement.bounds.x = geo.x;
				viewElement.bounds.y = geo.y;
				viewElement.bounds.width = geo.w;
				viewElement.bounds.height = geo.h;
			} else {
				viewElement.bounds = {x: geo.x, y: geo.y, width: geo.w, height: geo.h};
			}
			viewElement.x = geo.x;
			viewElement.y = geo.y;
			viewElement.w = geo.w;
			viewElement.h = geo.h;
			if (
				viewElement.$parent !==
				(geo.parentNodeId ? modeler._findViewElementById?.(geo.parentNodeId) : modeler._currentView)
			) {
				modeler._moveViewElementToParent?.(id, geo.parentNodeId);
			}
		};

		const DESCENDANT_LOCK_REFRESH_MS = 10000;
		let lastDescendantLockRefresh = Date.now();

		const refreshDescendantLocks = () => {
			if (typeof onLock !== 'function') return;
			const now = Date.now();
			if (now - lastDescendantLockRefresh < DESCENDANT_LOCK_REFRESH_MS) return;
			lastDescendantLockRefresh = now;
			const viewId = modeler.getActiveViewId?.() ?? null;
			pending.forEach((state, identifier) => {
				if (!state.followAncestor) return;
				onLock({
					action: 'acquire',
					objectId: identifier,
					kind: 'node',
					viewIndex: VIEW_INDEX,
					viewId,
				});
			});
		};

		const sendLive = () => {
			if (pending.size === 0) return;
			refreshDescendantLocks();
			if (typeof onLive !== 'function') return;
			const viewId = modeler.getActiveViewId?.() ?? null;
			pending.forEach((state, identifier) => {
				// Сдвиг вместе с предком применяет наблюдатель через setPosition({deep:true}).
				if (state.followAncestor) return;
				onLive({
					objectId: identifier,
					kind: 'node',
					viewIndex: VIEW_INDEX,
					viewId,
					x: state.current.x,
					y: state.current.y,
					w: state.current.w,
					h: state.current.h,
				});
			});
		};

		const scheduleLive = () => {
			const wait = LIVE_THROTTLE_MS - (Date.now() - lastLiveAt);
			if (wait <= 0) {
				lastLiveAt = Date.now();
				sendLive();
				return;
			}
			if (!liveTimer) {
				liveTimer = setTimeout(() => {
					liveTimer = null;
					lastLiveAt = Date.now();
					sendLive();
				}, wait);
			}
		};

		const changed = (start, current) =>
			start.x !== current.x ||
			start.y !== current.y ||
			start.w !== current.w ||
			start.h !== current.h ||
			start.parentNodeId !== current.parentNodeId;

		const commitPending = () => {
			if (liveTimer) {
				clearTimeout(liveTimer);
				liveTimer = null;
			}
			if (pending.size === 0) return;
			const viewId = modeler.getActiveViewId?.() ?? null;
			pending.forEach((state, identifier) => {
				modeler._geometryGestureIds?.delete(identifier);
				if (modeler._draggingNodeId === identifier) {
					modeler._draggingNodeId = null;
				}
				if (!changed(state.start, state.current)) {
					onLock?.({
						action: 'release',
						objectId: identifier,
						kind: 'node',
						viewIndex: VIEW_INDEX,
						viewId,
					});
					return;
				}
				onLock?.({
					action: 'acquire',
					objectId: identifier,
					kind: 'node',
					viewIndex: VIEW_INDEX,
					viewId,
					pendingPersist: true,
				});
				const parentChanged = state.start.parentNodeId !== state.current.parentNodeId;
				const forward = {
					op: 'updateNode',
					viewIndex: VIEW_INDEX,
					identifier,
					x: state.current.x,
					y: state.current.y,
					w: state.current.w,
					h: state.current.h,
					parentNodeId: state.current.parentNodeId,
					includeParent: parentChanged,
				};
				onPatch({patches: [forward]});
			});
			pending.clear();
		};

		const onNodeChange = args => {
			const cell = args?.cell ?? args?.node ?? args;
			if (args?.options?.skipPatchSync || modeler._applyingPatch) return;
			if (cell && typeof cell.isNode === 'function' && !cell.isNode()) return;
			const id = cell?.id;
			if (!id) return;
			if (modeler.isLockedByOther?.(id)) return;
			const followAncestor = Boolean(args?.options?.translateBy && args.options.translateBy !== id);
			if (!followAncestor) modeler._draggingNodeId = id;
			if (!modeler._geometryGestureIds) modeler._geometryGestureIds = new Set();
			modeler._geometryGestureIds.add(id);
			const viewElement = modeler._findViewElementById(id);
			if (!viewElement) return;
			const geo = readGeometry(cell, viewElement);
			if (!geo) return;
			applyLocalModel(id, viewElement, geo);
			const prev = pending.get(id);
			if (!prev) {
				pending.set(id, {start: {...geo}, current: geo, followAncestor});
			} else {
				prev.current = geo;
				if (!followAncestor) prev.followAncestor = false;
			}
			scheduleLive();
		};

		const onNodeMouseUp = () => {
			commitPending();
		};

		const onBatchStop = ({name}) => {
			if (name !== 'move-selection') return;
			commitPending();
		};

		const events = [
			'node:change:position',
			'node:change:size',
			'cell:change:position',
			'cell:change:size',
		];
		events.forEach(ev => graph.on(ev, onNodeChange));
		graph.on('node:mouseup', onNodeMouseUp);
		graph.on('node:resized', onNodeMouseUp);
		graph.on('blank:mouseup', onNodeMouseUp);
		graph.on('batch:stop', onBatchStop);

		return () => {
			if (liveTimer) clearTimeout(liveTimer);
			events.forEach(ev => graph.off(ev, onNodeChange));
			graph.off('node:mouseup', onNodeMouseUp);
			graph.off('node:resized', onNodeMouseUp);
			graph.off('blank:mouseup', onNodeMouseUp);
			graph.off('batch:stop', onBatchStop);
		};
	},
};
